import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { InvalidOperationException } from "Global/Base/Exceptions";
import { IDisposable } from "Global/Base/IDisposable";
import { ErrorHandler } from "UI/Errors";
import { IFormRight } from "UI/UI/Rights/FormRight";
import { DIContainer } from "../../../../Global/Base/DIContainer";
import { IHTMLElementWithControl } from "../Controls/ControlInterfaces";
import { IFRMParams } from "../Controls/FormParameters";
import { Registrator } from "../Controls/Registrator";
import { IFormUI } from "../FormUI/FormUI";
import { FormPurpose } from "../IFormUIFactory";
import { WindowState } from "../UIManagerInterfaces";
import { BaseForm, BaseFormParams } from "./BaseForm";
import { FormResult } from "./Forms/FormResult";
import { IFormSavingParameters } from "./IForm";
import { IFrame } from "./IFrame";

const logger = LazyLoadingSectionLogger.create(LoggingSection.widget);

export class WidgetForm extends BaseForm {
    //#region Fields
    private _hasContainer: boolean = false;
    private _framesInitialized: boolean = false;
    //#endregion Fields
    //#region Properties
    /**
     * Práva definována na úrovni formuláře
     */
    public override get formRights(): Array<IFormRight> {
        return [];
    }

    public get hasContainerInside(): boolean {
        return this._hasContainer;
    }
    //#endregion Properties

    //#region Constructors
    /**
     * Pro inicializaci je potřeba navíc volat metodu init
     * @param onwerForm
     * @param element
     * @param formInstanceId Identifikátor otevřené instance formuláře.
     * @param registerToBrowserHistory
     */
    constructor({ formInstanceId, registerToBrowserHistory = true, ...otherParams }: BaseFormParams) {
        super({ formInstanceId, registerToBrowserHistory, ...otherParams });
    }
    //#endregion Constructors

    //#region Methods
    /**
     * Inicializace formuláře
     */
    public override async init(): Promise<void> {
        try {
            this._canRead = true;
            // Inicializuje tsc controly u nalezených elementů
            await Registrator.initializeFormControls(this, this.element);

            this.formUI = DIContainer.instance.create(IFormUI, this, FormPurpose.standard);

            await this._initFrame();
        } catch (er) {
            ErrorHandler.instance.handleError(er);
        }
    }

    private async _initFrame(): Promise<void> {
        const frameElements: IHTMLElementWithControl[] = Array.from(this.element.querySelectorAll("[data-behaviour='frame']"));
        if (!frameElements.length) {
            return;
        }

        if (frameElements.length > 1) {
            logger.warn(`WidgetPanel doesn't support more than one Frame, number of frames: ${frameElements.length}`);
        }
        this._canNew = true;
        if (frameElements.length === 1) {
            const frameElement = frameElements[0];

            if (!(frameElement.tscControl instanceof IFrame)) {
                throw new InvalidOperationException(`Initializing component '${frameElement.getAttribute("data-tsccontrol")}' of data-behaviour 'frame' but it doesn't implement interface IFrame!`);
            }

            const frame = frameElement.tscControl;

            if (!frame.frameName) {
                logger.warn(`[${frameElement.getAttribute("data-tsccontrol")}] Frame has empty FrameName property`);
            }

            this._frames.push(frame);

            frame.initialize();
            this.framesInitialized.trigger(this, null);
            this._framesInitialized = true;
        }
    }

    /**
    * Metoda spouštěná pokud se zaktivuje formulář při práci s modálními okny
    */
    public override async checkAndSaveForm(_param: IFormSavingParameters = { sourceFrame: null, cancelEditIfNotEdited: true, checkRecommended: false, forceSave: false, formClosing: false }): Promise<boolean> {
        return true;
    }

    public override setState(state: WindowState, value: any): void {
        switch (state) {
            case WindowState.active:
                if (this._isActive != value) {
                    this._isActive = value;
                    if (value) {
                        BaseForm.resizeEventMethod();
                    }
                } else {
                    return;
                }
                break;
        }
        this.stateChanged.trigger(this, { state, value });
    }

    public override getState(state: WindowState): boolean {
        switch (state) {
            case WindowState.active:
                return this._isActive;
            default:
                return false;
        }
    }

    public override async setToolbarToEditMode(_inEdit: boolean): Promise<void> {
        // do nothing.
    }
    public override close(_result?: FormResult): Promise<boolean> {
        throw new Error("Widget cannot be closed.");
    }
    public setVisibleFrame(frame: IFrame, addToUIHistory?: boolean): void;
    public setVisibleFrame(frame: string, addToUIHistory?: boolean): void;
    public setVisibleFrame(frame: any, addToUIHistory?: boolean): void;
    public override setVisibleFrame(_frame: any, _addToUIHistory?: any): void {
        // do nothing.
    }
    public override async actualizeForm(): Promise<void> {
        // do nothing.
    }
    public override async waitForBussyFrameAndChildren(_sourceFrame: IFrame): Promise<void> {
        // do nothing.
    }
    public override setParametricData(_paramData: IFRMParams): void {
        // do nothing.
    }

    public override clearProfileInfo(): void {
        // do nothing.
    }

    public override addSidePanel(_panel: HTMLElement): void {
        // do nothing.
    }

    public override dispose(): void {
        if (this._framesInitialized) {
            super.dispose();
        } else {
            const frames: IHTMLElementWithControl[] = Array.from(this.element.querySelectorAll("[data-behaviour='frame']"));
            for (const frame of frames) {
                frame.parentElement.removeChild(frame);
            }

            const controls: IHTMLElementWithControl[] = Array.from(this.element.querySelectorAll("div[data-tsccontrol]"));

            for (const element of controls) {
                const control = element.tscControl;
                if (control instanceof IDisposable)
                    control.dispose();

                element.tscControl = null;
            }
        }
    }
    //#endregion Methods
}
